import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import AboutDeviceIcon from '@/components/Icons/AboutDeviceIcon';
import {MaxWidthContainer} from '@/components/Layout';
import {ABOUT_YOUR_DEVICE_TITLE} from '@/consts/localization/localizationKeys';
import routePaths from '@/consts/route/routePaths';
import DevicesSettingsSection from '@/containers/DevicesPage/components/DevicesSettingsSection/DevicesSettingsSection';
import useLayout from '@/hooks/layout/useLayout';
import useUamAssets from '@/hooks/uam/useUamAssets';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import amplitudeDevicesPageTracking from '@/services/analytics/amplitude/amplitudeDevicesPageTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import deviceTypesService from '@/services/device/deviceTypesService';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import {
    selectCurrentProductInfo,
    selectDeviceData,
    selectHolderData,
    selectIsDeviceDataLoading,
    selectIsSelectedDeviceConnectedToIot,
} from '@/state/selectors/device';
import {WARNING_BANNER_SOURCE_TYPES, WarningBanners, WarningBannersTop} from '@/views/WarningBanners';

import useOnConnectClick from '../../hooks/useOnConnectClick';
import AboutTitle from './components/AboutTitle';
import DeviceInfoBlock from './components/DeviceInfoBlock';
import FirmwareVersionWarning from './components/FirmwareVersionWarning';

const AboutDevicePage = () => {
    const {isMobile, isDesktop} = useLayout();
    const ref = useRef(null);
    const localizedStrings = getLocalizedStrings();
    const device = useSelector(selectCurrentProductInfo);
    const deviceData = useSelector(selectDeviceData) || {};
    const holderData = useSelector(selectHolderData) || {};
    const {codentify, holder, defaultHolderType, type, deviceSerialNumber} = device || {};
    const holderNode = holder || (defaultHolderType ? {type: defaultHolderType, isHolder: true} : undefined);
    const holderDefaultData = deviceTypesService.getDeviceTypesDataByType(holderNode?.type);
    const isDeviceDataLoading = useSelector(selectIsDeviceDataLoading);
    const isSelectedDeviceConnectedToIot = useSelector(selectIsSelectedDeviceConnectedToIot);

    const [isChargerError, setIsChargerError] = useState(false);
    const [isHolderError, setIsHolderError] = useState(false);
    const handleOnConnectClick = useOnConnectClick(type, deviceSerialNumber);

    useDocumentTitle(localizedStrings[ABOUT_YOUR_DEVICE_TITLE]);

    useEffect(() => {
        amplitudeDevicesPageTracking.trackAboutMyDeviceScreen();
        googleAnalyticsService.trackAboutMyDeviceScreenView(type, deviceSerialNumber);
    }, []);

    useUamAssets({
        isDisabled: isSelectedDeviceConnectedToIot,
        setIsChargerError,
        setIsHolderError,
        codentify,
        holderCodentify: holder?.codentify,
    });

    return (
        <div className='flex w-full flex-col'>
            <DevicesSettingsSection.Container
                title={localizedStrings[ABOUT_YOUR_DEVICE_TITLE]}
                onClick={handleOnConnectClick}
                pageName={routePaths.ABOUT_DEVICE}
                itemName='connectDeviceButton'
                className='ml:pb-2'
                isContentContainerFocusable
                ref={ref}
            >
                {isMobile && <WarningBannersTop source={WARNING_BANNER_SOURCE_TYPES.ABOUT_DEVICE} containerRef={ref} />}
                <MaxWidthContainer className='mx-auto w-full md:max-w-[500px] ml:max-w-none ml:items-start ml:px-0'>
                    {isMobile && <AboutDeviceIcon />}
                    <AboutTitle device={device} />
                    <FirmwareVersionWarning className='mt-8' />
                    <div className='mb-6 mt-4 w-full rounded-xl bg-primary-dark-5 p-4 text-start ml:mt-8 ml:bg-transparent ml:p-0'>
                        <DeviceInfoBlock
                            modelLabelKey='chargerModel'
                            codentifyLabelKey='chargerCodentify'
                            firmwareLabelKey='chargerFirmware'
                            node={device}
                            nodeData={isChargerError ? undefined : deviceData}
                            loading={isDeviceDataLoading}
                        />
                        {holderNode && (
                            <DeviceInfoBlock
                                modelLabelKey='holderModel'
                                codentifyLabelKey='holderCodentify'
                                firmwareLabelKey='holderFirmware'
                                node={holderNode}
                                nodeData={isHolderError ? undefined : holderData}
                                holderDefaultData={holderDefaultData}
                                loading={isDeviceDataLoading}
                            />
                        )}
                    </div>
                </MaxWidthContainer>
                {isMobile && <WarningBanners source={WARNING_BANNER_SOURCE_TYPES.ABOUT_DEVICE} />}
            </DevicesSettingsSection.Container>
            {isDesktop && <WarningBanners source={WARNING_BANNER_SOURCE_TYPES.DEVICES} />}
        </div>
    );
};

export default AboutDevicePage;
